import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";

import type { SupportTicketDetail } from "../../shared/support-schemas";
import { ContentLoading } from "../components/ContentLoading";
import { Badge } from "../components/ui/badge";
import { FieldError } from "../components/ui/field";
import { cn } from "../lib/utils";
import type { OperationsNavigate } from "./navigation";
import { OperationsLink } from "./OperationsLink";
import { PAGE_WIDTH } from "./page-width";
import { getSupportTicket, listSupportAssignees, LIVE_TICKET, supportKeys } from "./support-api";
import { CHANNEL_LABELS, requesterLabel, STATUS_BADGES, STATUS_LABELS } from "./support-format";
import { SupportComposer } from "./SupportComposer";
import { SupportThread } from "./SupportThread";
import { SupportTicketActivity } from "./SupportTicketActivity";
import { SupportTicketContext } from "./SupportTicketContext";
import { SupportTicketFields } from "./SupportTicketFields";

/**
 * One ticket, laid out like a helpdesk agent workspace (ADR-0089): its fields on the left, the
 * conversation and reply box in the middle, and what we know about the customer on the right.
 */
export function SupportTicketPage({
  ticketId,
  navigate,
  canWrite,
}: {
  ticketId: string;
  navigate: OperationsNavigate;
  canWrite: boolean;
}) {
  const ticket = useQuery({
    queryKey: supportKeys.ticket(ticketId),
    queryFn: () => getSupportTicket(ticketId),
    ...LIVE_TICKET,
  });
  if (ticket.isPending)
    return (
      <div className={cn("mx-auto w-full p-6 sm:p-8", PAGE_WIDTH)}>
        <ContentLoading label="Loading ticket…" variant="document" />
      </div>
    );
  if (ticket.error)
    return (
      <div className={cn("mx-auto flex w-full flex-col gap-4 p-6 sm:p-8", PAGE_WIDTH)}>
        <BackLink navigate={navigate} />
        <FieldError>{ticket.error.message}</FieldError>
      </div>
    );
  return <TicketWorkspace ticket={ticket.data.ticket} navigate={navigate} canWrite={canWrite} />;
}

function TicketWorkspace({
  ticket,
  navigate,
  canWrite,
}: {
  ticket: SupportTicketDetail;
  navigate: OperationsNavigate;
  canWrite: boolean;
}) {
  const queryClient = useQueryClient();
  const assignees = useQuery({
    queryKey: supportKeys.assignees(ticket.organizationId),
    queryFn: () => listSupportAssignees(ticket.organizationId),
  });
  const people = assignees.data?.assignees ?? [];
  const names = new Map(people.map((person) => [person.employeeId, person.displayName]));
  if (ticket.assignee) names.set(ticket.assignee.employeeId, ticket.assignee.displayName);
  const replace = (next: SupportTicketDetail) => {
    queryClient.setQueryData(supportKeys.ticket(ticket.id), { ticket: next });
    void queryClient.invalidateQueries({ queryKey: [...supportKeys.all, "list"] });
    void queryClient.invalidateQueries({ queryKey: supportKeys.views() });
  };
  return (
    <div className={cn("mx-auto flex w-full flex-col gap-6 p-6 sm:p-8", PAGE_WIDTH)}>
      <TicketHeader ticket={ticket} navigate={navigate} />
      <div className="grid items-start gap-6 lg:grid-cols-[16rem_minmax(0,1fr)] xl:grid-cols-[16rem_minmax(0,1fr)_18rem]">
        <SupportTicketFields
          ticket={ticket}
          assignees={people}
          canWrite={canWrite}
          onUpdated={replace}
        />
        <div className="flex min-w-0 flex-col gap-4">
          <SupportThread ticket={ticket} />
          {canWrite ? <SupportComposer ticket={ticket} onChanged={replace} /> : null}
        </div>
        <div className="flex min-w-0 flex-col gap-6 lg:col-start-2 xl:col-start-auto">
          <SupportTicketContext
            ticket={ticket}
            navigate={navigate}
            canWrite={canWrite}
            onUpdated={replace}
          />
          <SupportTicketActivity ticket={ticket} names={names} />
        </div>
      </div>
    </div>
  );
}

function TicketHeader({
  ticket,
  navigate,
}: {
  ticket: SupportTicketDetail;
  navigate: OperationsNavigate;
}) {
  return (
    <header className="flex flex-col gap-2">
      <BackLink navigate={navigate} />
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
        <span className="font-mono">{ticket.reference}</span>
        <Badge variant={STATUS_BADGES[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
        <span>via {CHANNEL_LABELS[ticket.channel]}</span>
      </div>
      <h1 className="text-3xl font-extrabold tracking-tight">{ticket.subject}</h1>
      <p className="text-muted-foreground">
        {requesterLabel(ticket.requester)} · {ticket.customerName}
      </p>
    </header>
  );
}

function BackLink({ navigate }: { navigate: OperationsNavigate }) {
  return (
    <OperationsLink
      href="/support"
      navigate={navigate}
      className="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-link hover:underline"
    >
      <ArrowLeft aria-hidden="true" className="size-4" />
      Support
    </OperationsLink>
  );
}
